import { useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ModalsWrapperContext } from '@kanda-libs/ks-design-library';
import { Onboarding } from '@kanda-libs/ks-frontend-services';

import { APP_ENV } from 'common/constants';
import { capitaliseWords } from 'common/helpers';
import { selectors } from 'store';

import { MODAL_ID, PROD_LINK, QA_LINK } from './constants';

export interface OnboardingHook {
  stage: string;
  link?: string;
  trainingStatus?: string;
  onClick: () => void;
}

const formatOnboardingLink = (onboarding: Onboarding): string | undefined => {
  const cid = onboarding?.cid;
  if (!cid) return undefined;
  const base = APP_ENV === 'qa' ? QA_LINK : PROD_LINK;
  return `${base}${cid}`;
};

export default function useOnboarding(): OnboardingHook {
  const onboarding = useSelector(selectors.onboarding.getEntity);
  const company = useSelector(selectors.company.getEntity);
  const { showModal } = useContext(ModalsWrapperContext);

  const stage = useMemo(() => {
    if (!onboarding) return 'No current stage';
    return onboarding?.current_stage || 'No current stage';
  }, [onboarding]);

  const link = useMemo(() => {
    if (!onboarding) return undefined;
    return formatOnboardingLink(onboarding);
  }, [onboarding]);

  const trainingStatus = useMemo(() => {
    if (!company) return undefined;
    const status = company?.training_status;
    if (!status) return 'No training status';
    return capitaliseWords(status.replaceAll('_', ' '));
  }, [company]);

  const onClick = useCallback(() => showModal(MODAL_ID), [showModal]);

  return {
    stage,
    link,
    trainingStatus,
    onClick,
  };
}
